import { format as dateFnsFormat } from "date-fns";

function decimalNumber(num: number, maxDigits?: number): string {
  const options = Number.isInteger(number) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  const numberNum = Number(num);

  // If the number is 0, return it as a string
  if (numberNum === 0) return numberNum.toString();

  if (maxDigits)
    return new Intl.NumberFormat("en-UK", { minimumFractionDigits: maxDigits, maximumFractionDigits: maxDigits }).format(
      numberNum
    );

  // Every threshold below is about how big the number is, never which side of zero it sits on. They used
  // to compare the signed value, so a negative never reached the extra-decimal branches and fell through
  // to the 2-decimal formatter: 0.002 printed "0.002" while -0.002 printed "-0.00", i.e. a real credit
  // rendered as minus zero (RTLCA-5189 follow-up). Compare the magnitude, and negatives mirror positives.
  const magnitude = Math.abs(numberNum);

  // Use scientific notation for numbers smaller than 0.0001 or larger than 999999999.99
  if (magnitude < 0.0001 || magnitude > 999999999.99) {
    return numberNum.toExponential(2);
  } else if (magnitude < 0.01 && magnitude >= 0.001) {
    return numberNum.toFixed(3);
  } else if (magnitude < 0.001 && magnitude >= 0.00001) {
    return numberNum.toFixed(4);
  } else {
    return new Intl.NumberFormat("en-UK", options).format(numberNum);
  }
}

function number(number: number) {
  // Only applies decimals if the number is not an integer
  const options = Number.isInteger(number) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return new Intl.NumberFormat("en-UK", options).format(number);
}

function numberWithDecimals(number: number, digits: number = 2) {
  // Only applies decimals if the number is not an integer
  const options = {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  };
  return new Intl.NumberFormat("en-UK", options).format(number);
}

function date(date: string | Date) {
  if (!date) return "";

  if (typeof date === "string") {
    date = new Date(date);
  }

  return dateFnsFormat(date, "yyyy-MM-dd");
}

// European day/month/year with a 2-digit year, e.g. 31/12/26
function dateShort(date: string | Date) {
  if (!date) return "";

  if (typeof date === "string") {
    date = new Date(date);
  }

  return dateFnsFormat(date, "dd/MM/yy");
}

function capitalize(string: string) {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export default { capitalize, number, numberWithDecimals, date, dateShort, decimalNumber };
